import { getTokens } from '../utils/tokens';
import { getAxiosInstance } from '../utils/apiClient';
import { cancelSubscriptionAPI } from '../api/ccbillApi';

const tokens = getTokens();
const apiClient = getAxiosInstance();

export const subscriptionService = {
    /**
     * Get all available subscription plans
     * @returns {Promise<{success: boolean, data: {plans: Array}}>}
     */
    async getPlans() {
        try {
            const response = await apiClient.get('/api/monetization/subscriptions/plans', {
                headers: {
                    "Content-Type": "application/json",
                    [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
                    [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
                        `Bearer ${tokens.firebase_token}`,
                    "ngrok-skip-browser-warning": "true",
                },
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching subscription plans:', error);
            throw error;
        }
    },

    /**
     * Start a new subscription with CCBill payment
     * @param {string} tier - Subscription tier ('onyx' | 'sapphire' | 'diamond' | 'platinum_vip')
     * @param {number} amount - Subscription amount
     * @returns {Promise<{success: boolean, data: any}>}
     */
    async startSubscription(tier, amount = undefined, recipientId) {
        try {
            // First, process payment through CCBill
            const paymentResponse = await apiClient.post('/api/ccbill/payment/oneclick', {
                recipientId,
                amount: amount,
                context: "subscription",
                description: `${tier.charAt(0).toUpperCase() + tier.slice(1)} Monthly Subscription`
            }, {
                headers: {
                    "Content-Type": "application/json",
                    [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
                    [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
                        `Bearer ${tokens.firebase_token}`,
                    "ngrok-skip-browser-warning": "true",
                },
            });

            // If payment is successful, start the subscription
            if (paymentResponse.data.success) {
                const subscriptionResponse = await apiClient.post('/api/monetization/subscriptions/start', {
                    tier
                }, {
                    headers: {
                        "Content-Type": "application/json",
                        [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
                        [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
                            `Bearer ${tokens.firebase_token}`,
                        "ngrok-skip-browser-warning": "true",
                    },
                });
                return subscriptionResponse.data;
            } else {
                throw new Error(paymentResponse.data.message || 'Payment failed');
            }
        } catch (error) {
            console.error('Error starting subscription:', error);
            throw error;
        }
    },

    /**
     * Cancel current subscription (both database and CCBill)
     * @param {string} userId - User ID for CCBill cancellation (optional)
     * @returns {Promise<{success: boolean, data: any}>}
     */
    async cancelSubscription(userId = null) {
        try {
            // First, cancel subscription in our database
            const dbResponse = await apiClient.post('/api/monetization/subscriptions/cancel', {}, {
                headers: {
                    "Content-Type": "application/json",
                    [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
                    [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
                        `Bearer ${tokens.firebase_token}`,
                    "ngrok-skip-browser-warning": "true",
                },
            });

            // If database cancellation is successful, cancel CCBill subscription
            if (dbResponse.data.success && userId) {
                try {
                    await cancelSubscriptionAPI(userId);
                } catch (ccbillError) {
                    // Don't throw error here - database cancellation was successful
                }
            }

            return dbResponse.data;
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            throw error;
        }
    },

    /**
     * Change subscription to a new tier (automatically determines upgrade/downgrade)
     * @param {string} tier - Target subscription tier ('freemium' | 'onyx' | 'sapphire' | 'diamond' | 'platinum_vip')
     * @param {string} paymentMethod - Optional payment method ('ccbill' | 'stripe' | 'paypal')
     * @returns {Promise<{success: boolean, data: any}>}
     */
    async changeSubscription(tier, paymentMethod = 'ccbill') {
        try {
            const body = { tier };
            if (paymentMethod) {
                body.paymentMethod = paymentMethod;
            }

            const response = await apiClient.post('/api/monetization/subscriptions/change', body, {
                headers: {
                    "Content-Type": "application/json",
                    [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
                    [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
                        `Bearer ${tokens.firebase_token}`,
                    "ngrok-skip-browser-warning": "true",
                },
            });
            return response.data;
        } catch (error) {
            console.error('Error changing subscription:', error);
            throw error;
        }
    },

    /**
     * Upgrade subscription to a new tier (legacy - use changeSubscription instead)
     * @param {string} newTier - New subscription tier
     * @returns {Promise<{success: boolean, data: any}>}
     */
    async upgradeSubscription(newTier) {
        return this.changeSubscription(newTier);
    },

    /**
     * Downgrade subscription to a new tier (legacy - use changeSubscription instead)
     * @param {string} newTier - New subscription tier
     * @returns {Promise<{success: boolean, data: any}>}
     */
    async downgradeSubscription(newTier) {
        return this.changeSubscription(newTier);
    }
};

export default subscriptionService;
